import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  /** Allow choosing a typed value that isn't in the list (e.g. a town not in the dataset). */
  allowCustom?: boolean;
  className?: string;
}

const MAX_VISIBLE = 50;

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  disabled,
  allowCustom,
  className,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  // Manual filtering (cmdk's built-in filter is disabled) so we can cap the rendered
  // list — some countries have thousands of cities and rendering them all is slow.
  const q = query.trim().toLowerCase();
  const matched = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  const visible = matched.slice(0, MAX_VISIBLE);
  const showCustom =
    allowCustom && q.length > 0 && !options.some((o) => o.label.toLowerCase() === q);

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("h-10 w-full justify-between font-normal", !value && "text-muted-foreground", className)}
        >
          <span className="truncate">{value ? selectedLabel : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {visible.length === 0 && !showCustom && <CommandEmpty>{emptyText}</CommandEmpty>}
            <CommandGroup>
              {visible.map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => pick(o.value)}>
                  <Check className={cn("mr-2 h-4 w-4", value === o.value ? "opacity-100" : "opacity-0")} />
                  {o.label}
                </CommandItem>
              ))}
              {showCustom && (
                <CommandItem value={`__custom__${query}`} onSelect={() => pick(query.trim())}>
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use “{query.trim()}”
                </CommandItem>
              )}
            </CommandGroup>
            {matched.length > visible.length && (
              <p className="px-3 py-2 text-xs text-muted-foreground">
                Showing first {MAX_VISIBLE} of {matched.length} — keep typing to narrow.
              </p>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
